import type { ColorSpec } from './types';

/**
 * White color spec — uses the `paper` Tailwind palette.
 *
 * Filled = pure white (paper-50) with dark text and a barely-visible
 * paper-200 border. Outline = same white bg but with a **gray paper-400
 * border** (the explicit design requirement). Soft = barely-tinted
 * paper-100 bg.
 *
 * In dark mode the bg darkens toward paper-900 / 950 to stay readable
 * against the dark canvas — same "swap palette ends" pattern the yellow
 * spec uses. If a future design wants white to *stay* white in dark
 * mode, swap the `dark:bg-…` shades back toward the light end.
 */
export const white: ColorSpec = {
  filled: {
    border: 'border-paper-200 dark:border-paper-700',
    bg: 'bg-paper-50 text-paper-800 dark:bg-paper-900 dark:text-paper-100',
    bgActive: 'bg-paper-200 text-paper-900 dark:bg-paper-700 dark:text-paper-50',
    hover: 'hover:bg-paper-100 hover:text-paper-900 active:bg-paper-200 active:text-paper-900',
    hoverDark: 'dark:hover:bg-paper-800 dark:hover:text-paper-50 dark:active:bg-paper-700 dark:active:text-paper-50',
  },
  outline: {
    border: 'border-paper-400 dark:border-paper-500',
    bg: 'bg-paper-50 dark:bg-paper-900 text-paper-800 dark:text-paper-100',
    bgActive: 'bg-paper-200 dark:bg-paper-700 text-paper-900 dark:text-paper-50',
    hover: 'hover:bg-paper-100 hover:text-paper-900 active:bg-paper-200 active:text-paper-900',
    hoverDark: 'dark:hover:bg-paper-800 dark:hover:text-paper-50 dark:active:bg-paper-700 dark:active:text-paper-50',
    hoverWhenActive: 'hover:bg-paper-300 dark:hover:bg-paper-600',
  },
  soft: {
    border: 'border-paper-100 dark:border-paper-800',
    bg: 'bg-paper-100 dark:bg-paper-900 text-paper-800 dark:text-paper-100 font-normal',
    bgActive: 'bg-paper-300 dark:bg-paper-700 text-paper-900 dark:text-paper-50',
    hover: 'hover:bg-paper-300 hover:text-paper-900 active:bg-paper-400 active:text-paper-900',
    hoverDark: 'dark:hover:bg-paper-700 dark:hover:text-paper-50 dark:active:bg-paper-600 dark:active:text-paper-50',
    hoverWhenActive: 'hover:bg-paper-400 dark:hover:bg-paper-600',
  },
  textFilled: {
    text: 'text-paper-800 dark:text-paper-200',
    activeBg: 'bg-paper-200 text-paper-900',
    hover: 'hover:bg-paper-100 active:bg-paper-200',
    hoverDark: 'dark:hover:bg-paper-800 dark:active:bg-paper-700',
  },
  textSoft: {
    text: 'text-paper-800 dark:text-paper-200 font-normal',
    activeBg: 'bg-paper-300 text-paper-900 dark:bg-paper-700 dark:text-paper-50',
    hover: 'hover:bg-paper-100 hover:text-paper-900 active:bg-paper-200',
    hoverDark: 'dark:hover:bg-paper-800 dark:hover:text-paper-50 dark:active:bg-paper-700',
  },
  tooltip:
    'bg-paper-50 dark:bg-paper-800 text-paper-800 dark:text-paper-100 border border-paper-200 dark:border-paper-700',
  badge: {
    filled: 'bg-paper-600 dark:bg-paper-700 text-white',
    soft: 'bg-paper-400 dark:bg-paper-500 text-white',
  },
  softIcon: 'text-paper-800 dark:text-paper-200 group-hover/btn:text-current',
  underline: {
    filled: '[&_u]:text-paper-900 dark:[&_u]:text-paper-100 [&_u]:decoration-paper-700 dark:[&_u]:decoration-paper-300',
    outline:
      '[&_u]:text-paper-900 dark:[&_u]:text-paper-100 [&_u]:decoration-paper-700 dark:[&_u]:decoration-paper-300',
    soft: '[&_u]:text-paper-900 dark:[&_u]:text-paper-100 [&_u]:decoration-paper-500 dark:[&_u]:decoration-paper-400',
  },
  input: {
    filled: {
      border: 'border-paper-200 dark:border-paper-700',
      bg: 'bg-paper-50 dark:bg-paper-900',
      focusBg: 'focus-within:bg-paper-100 dark:focus-within:bg-paper-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-paper-300 dark:focus-within:outline-paper-600',
      hoverActive:
        'hover:bg-paper-100 hover:text-paper-900 active:bg-paper-200 dark:hover:bg-paper-800 dark:hover:text-paper-50 dark:active:bg-paper-700',
      readOnlyBg: 'bg-paper-100 dark:bg-paper-900',
      text: 'text-paper-800 dark:text-paper-100',
    },
    outline: {
      border: 'border-paper-400 dark:border-paper-500',
      bg: 'bg-paper-50 dark:bg-paper-900',
      focusBg: 'focus-within:bg-paper-100 dark:focus-within:bg-paper-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-paper-400 dark:focus-within:outline-paper-500',
      hoverActive:
        'hover:bg-paper-100 hover:text-paper-900 active:bg-paper-200 active:text-paper-900 dark:hover:bg-paper-800 dark:hover:text-paper-50 dark:active:bg-paper-700',
      readOnlyBg: 'bg-paper-50 dark:bg-paper-900',
      text: 'text-paper-800 dark:text-paper-100',
    },
    soft: {
      border: 'border-paper-100 dark:border-paper-800',
      bg: 'bg-paper-100 dark:bg-paper-900',
      focusBg: 'focus-within:bg-paper-200 dark:focus-within:bg-paper-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-paper-200 dark:focus-within:outline-paper-700',
      hoverActive:
        'hover:bg-paper-200 hover:text-paper-900 active:bg-paper-300 active:text-paper-900 dark:hover:bg-paper-800 dark:hover:text-paper-50 dark:active:bg-paper-700',
      readOnlyBg: 'bg-paper-100 dark:bg-paper-900',
      text: 'text-paper-800 dark:text-paper-100',
    },
  },
  inputMuted: {
    filled: 'text-paper-500',
    outline: 'text-paper-500 dark:text-paper-500',
    soft: 'text-paper-500 dark:text-paper-500',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-paper-500',
    outline: 'placeholder:text-paper-500 dark:placeholder:text-paper-500',
    soft: 'placeholder:text-paper-500 dark:placeholder:text-paper-500',
  },
  group: {
    filled: 'border-paper-200 dark:border-paper-700 bg-paper-50 dark:bg-paper-900',
    outline: 'border-paper-400 dark:border-paper-500 bg-paper-50 dark:bg-paper-900',
    soft: 'border-paper-100 dark:border-paper-800 bg-paper-100 dark:bg-paper-900',
  },
  groupDivider: {
    filled: 'bg-paper-200 dark:bg-paper-700',
    outline: 'bg-paper-300 dark:bg-paper-600',
    soft: 'bg-paper-200 dark:bg-paper-700',
  },
  segment: {
    filled: 'border-paper-200 dark:border-paper-700 bg-paper-50 dark:bg-paper-900',
    outline: 'border-paper-400 dark:border-paper-500 bg-paper-50 dark:bg-paper-900',
  },
  segmentDivider: {
    filled: 'bg-paper-200 dark:bg-paper-700',
    outline: 'bg-paper-300 dark:bg-paper-600',
  },
  popoverHighlight: 'bg-paper-200 text-paper-900',
  popoverText: 'text-paper-800 dark:text-paper-100',
  popoverBorder: 'border-paper-200 dark:border-paper-700',
};
